/**
 * Compatibility (Ashtakoot 36-गुण) v2 — constructive-doctrinal report.
 *
 * Score is present (honesty floor) but deemphasised — the headline is
 * "what works" + "what to manage". Classical pariharas are applied so
 * couples are not unnecessarily rejected. Management steps are
 * practical + Ayurvedic + conduct-aligned (no graha-puja).
 */
import { useCallback, useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';

import { ApiError, api } from '@/api/client';
import type {
  ChartCreateRequest,
  CompatibilityDoshaEntry,
  CompatibilityKootEntry,
  CompatibilityResponse,
} from '@/api/types';
import { ChartInputForm } from '@/components/ChartInputForm';
import { useLang } from '@/i18n/useLang';

export function CompatibilityPage() {
  const { lang, setLang } = useLang();
  const t = (hi: string, en: string) => (lang === 'hi' ? hi : en);

  const [boyChart, setBoyChart] = useState<ChartCreateRequest | null>(null);
  const [girlChart, setGirlChart] = useState<ChartCreateRequest | null>(null);
  const [pdfDownloading, setPdfDownloading] = useState(false);

  const reportMut = useMutation<
    CompatibilityResponse, Error,
    { boy: ChartCreateRequest; girl: ChartCreateRequest }
  >({
    mutationFn: (req) => api.compatibility(req),
  });

  const onSubmit = useCallback(() => {
    if (boyChart && girlChart) {
      reportMut.mutate({ boy: boyChart, girl: girlChart });
    }
  }, [boyChart, girlChart, reportMut]);

  const submitError = useMemo(() => {
    if (!reportMut.error) return null;
    const e = reportMut.error;
    if (e instanceof ApiError) {
      if (e.status === 422) return t('अमान्य इनपुट', 'Invalid input');
    }
    return e.message ?? 'Error';
  }, [reportMut.error, lang]);

  const report = reportMut.data;
  const ready = boyChart != null && girlChart != null;

  const downloadPdf = useCallback(async () => {
    if (!boyChart || !girlChart) return;
    try {
      setPdfDownloading(true);
      const blob = await api.compatibilityPdfBlob({ boy: boyChart, girl: girlChart }, lang);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `compatibility_${boyChart.name}_${girlChart.name}.pdf`.replace(/\s+/g, '_');
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('PDF download failed', e);
      alert(t('PDF डाउनलोड में त्रुटि', 'PDF download failed'));
    } finally {
      setPdfDownloading(false);
    }
  }, [boyChart, girlChart, lang, t]);

  return (
    <div className="mx-auto flex max-w-4xl flex-col gap-4 p-4 lg:gap-6 lg:p-6">
      <header className="flex items-start justify-between gap-3 rounded-2xl bg-stone-900/80 px-4 py-3 ring-1 ring-stone-800">
        <div>
          <h1 className="text-lg font-semibold text-saffron-100">
            {t('कुण्डली मिलान — विवाह संगति', 'Marriage Compatibility')}
          </h1>
          <p className="mt-1 text-xs text-stone-400">
            {t(
              'अष्टकूट 36-गुण मिलान, शास्त्रोक्त परिहार-जाँच, एवं प्रबंधन-निर्देश।',
              'Ashtakoot 36-गुण matching, classical parihara checks, and management guidance.',
            )}
          </p>
        </div>
        <div className="flex shrink-0 overflow-hidden rounded-lg ring-1 ring-stone-700">
          <button
            type="button"
            onClick={() => setLang('hi')}
            className={
              'px-3 py-1.5 text-xs font-medium transition '
              + (lang === 'hi'
                ? 'bg-saffron-600 text-stone-950'
                : 'bg-stone-800/60 text-stone-300 hover:bg-stone-800')
            }
            aria-pressed={lang === 'hi'}
          >
            हिन्दी
          </button>
          <button
            type="button"
            onClick={() => setLang('en')}
            className={
              'px-3 py-1.5 text-xs font-medium transition '
              + (lang === 'en'
                ? 'bg-saffron-600 text-stone-950'
                : 'bg-stone-800/60 text-stone-300 hover:bg-stone-800')
            }
            aria-pressed={lang === 'en'}
          >
            English
          </button>
        </div>
      </header>

      <section className="space-y-5">
        <div className="rounded-2xl bg-stone-900/40 p-3 ring-1 ring-stone-800">
          <h3 className="mb-2 text-sm font-semibold text-saffron-300">
            1. {t('वर / पुरुष — जन्म विवरण', 'Boy / Groom — Birth Details')}
          </h3>
          <ChartInputForm onSubmit={(req) => setBoyChart(req)} isSubmitting={false} />
          {boyChart && (
            <p className="mt-2 text-xs text-emerald-400">
              ✓ {t('दर्ज', 'Saved')}: {boyChart.name}
            </p>
          )}
        </div>
        <div className="rounded-2xl bg-stone-900/40 p-3 ring-1 ring-stone-800">
          <h3 className="mb-2 text-sm font-semibold text-saffron-300">
            2. {t('वधू / स्त्री — जन्म विवरण', 'Girl / Bride — Birth Details')}
          </h3>
          <ChartInputForm onSubmit={(req) => setGirlChart(req)} isSubmitting={false} />
          {girlChart && (
            <p className="mt-2 text-xs text-emerald-400">
              ✓ {t('दर्ज', 'Saved')}: {girlChart.name}
            </p>
          )}
        </div>
      </section>

      <button
        type="button"
        disabled={!ready || reportMut.isPending}
        onClick={onSubmit}
        className="rounded-md bg-saffron-600 px-4 py-2 font-semibold text-stone-950 transition hover:bg-saffron-500 disabled:cursor-not-allowed disabled:bg-stone-700 disabled:text-stone-500"
      >
        {reportMut.isPending
          ? t('विश्लेषण हो रहा है…', 'Analysing…')
          : t('मिलान करें', 'Compute Match')}
      </button>

      {submitError && (
        <p className="rounded-2xl bg-rose-950/60 p-4 text-rose-200 ring-1 ring-rose-900">
          {submitError}
        </p>
      )}

      {report && (
        <ReportView
          report={report}
          lang={lang}
          t={t}
          onDownloadPdf={downloadPdf}
          pdfDownloading={pdfDownloading}
        />
      )}
    </div>
  );
}

function ReportView({
  report,
  lang,
  t,
  onDownloadPdf,
  pdfDownloading,
}: {
  report: CompatibilityResponse;
  lang: 'hi' | 'en';
  t: (hi: string, en: string) => string;
  onDownloadPdf: () => void;
  pdfDownloading: boolean;
}) {
  return (
    <article className="space-y-4">
      {/* Header card with PDF download — score is present but small */}
      <section className="flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-gradient-to-br from-saffron-900/50 to-stone-900 p-5 ring-2 ring-saffron-700/50">
        <div>
          <p className="text-xs uppercase tracking-wider text-saffron-300">
            {t('विवाह संगति विश्लेषण', 'Marriage Compatibility')}
          </p>
          <p className="mt-1 text-base font-medium text-stone-200">
            {report.boy_input.name} <span className="text-stone-500">·</span>{' '}
            {report.girl_input.name}
          </p>
          <p className="mt-1 text-[11px] text-stone-500">
            {t('कुल अंक', 'Total points')}: {report.total_points} / {report.max_total}
          </p>
        </div>
        <button
          type="button"
          onClick={onDownloadPdf}
          disabled={pdfDownloading}
          className="rounded-xl bg-saffron-600 px-4 py-2 text-sm font-medium text-stone-950 hover:bg-saffron-500 disabled:opacity-50"
        >
          {pdfDownloading
            ? t('बन रहा है…', 'Generating…')
            : t('PDF डाउनलोड', 'Download PDF')}
        </button>
      </section>

      {/* Headline (constructive) */}
      <section className="rounded-2xl bg-stone-950/40 p-5 ring-1 ring-stone-800">
        <p className="text-base leading-relaxed text-stone-100">
          {lang === 'hi' ? report.headline_hi : report.headline_en}
        </p>
      </section>

      {/* What works */}
      <section className="rounded-2xl bg-emerald-950/20 p-5 ring-1 ring-emerald-800/40">
        <h2 className="mb-3 text-base font-semibold text-emerald-200">
          {t('क्या अच्छा है', 'What Works')}
        </h2>
        <ul className="space-y-2 text-sm leading-relaxed text-stone-100">
          {(lang === 'hi' ? report.what_works_hi : report.what_works_en).map((w, i) => (
            <li key={i} className="flex gap-2">
              <span className="text-emerald-400">●</span>
              <span>{w}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* What to manage (only if there's anything to manage) */}
      {report.what_to_manage_en.length > 0 && (
        <section className="rounded-2xl bg-amber-950/20 p-5 ring-1 ring-amber-800/40">
          <h2 className="mb-3 text-base font-semibold text-amber-200">
            {t('किसका ध्यान रखें', 'What to Manage')}
          </h2>
          <ul className="space-y-2 text-sm leading-relaxed text-stone-100">
            {(lang === 'hi' ? report.what_to_manage_hi : report.what_to_manage_en).map((w, i) => (
              <li key={i} className="flex gap-2">
                <span className="text-amber-400">●</span>
                <span>{w}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Per-koot detail */}
      <section className="rounded-2xl bg-stone-950/40 p-5 ring-1 ring-stone-800">
        <h2 className="mb-1 text-base font-semibold text-saffron-200">
          {t('अष्टकूट विश्लेषण', 'Eight-Koot Analysis')}
        </h2>
        <p className="mb-3 text-xs text-stone-500">
          {t(
            'हर कूट का सकारात्मक पठन, एवं कम अंक होने पर व्यावहारिक उपाय।',
            'Constructive reading of every koot, with practical steps if any score is low.',
          )}
        </p>
        <div className="space-y-2">
          {report.koots.map((k) => (
            <KootCard koot={k} lang={lang} key={k.koot_id} />
          ))}
        </div>
      </section>

      {/* Doshas — only show fired ones */}
      {report.doshas.length > 0 && (
        <section className="rounded-2xl bg-stone-950/40 p-5 ring-1 ring-stone-800">
          <h2 className="mb-1 text-base font-semibold text-saffron-200">
            {t('दोष-विश्लेषण एवं परिहार', 'Dosha Analysis & Pariharas')}
          </h2>
          <p className="mb-3 text-xs text-stone-500">
            {t(
              'शास्त्रोक्त परिहार-जाँच लागू — जहाँ संभव दोष शान्त किया गया।',
              'Classical pariharas applied — wherever possible the dosha is pacified.',
            )}
          </p>
          <div className="space-y-3">
            {report.doshas.map((d) => (
              <DoshaCard d={d} lang={lang} t={t} key={d.dosha_id} />
            ))}
          </div>
        </section>
      )}

      {/* Joint path */}
      <section className="rounded-2xl bg-gradient-to-br from-saffron-900/40 to-stone-900 p-5 ring-1 ring-saffron-700/40">
        <h2 className="mb-2 text-base font-semibold text-saffron-200">
          {t('संयुक्त पथ', 'Joint Path')}
        </h2>
        <p className="text-sm leading-relaxed text-stone-100">
          {lang === 'hi' ? report.joint_path_hi : report.joint_path_en}
        </p>
      </section>

      {/* Methodology footer */}
      <section className="rounded-2xl bg-stone-900/20 p-3 ring-1 ring-stone-800/40">
        <h3 className="mb-1.5 text-[9px] font-semibold uppercase tracking-wider text-stone-500">
          {t('प्रयुक्त ज्योतिष-विधि', 'Methodology Used')}
        </h3>
        <ul className="space-y-0.5 text-[8.5px] leading-snug text-stone-500">
          {report.methods.map((m, i) => (
            <li key={i}>
              <span className="text-stone-400">{lang === 'hi' ? m.method_hi : m.method_en}</span>
              <span className="text-stone-600"> — {lang === 'hi' ? m.source_hi : m.source_en}</span>
            </li>
          ))}
        </ul>
        <p className="mt-2 border-t border-stone-800/40 pt-1.5 text-[8.5px] italic leading-snug text-stone-600">
          {lang === 'hi' ? report.disclaimer_hi : report.disclaimer_en}
        </p>
        <p className="mt-1 text-[8px] text-stone-700">{report.rules_version}</p>
      </section>
    </article>
  );
}

// ─── Subcomponents ──────────────────────────────────────────────────────────

function KootCard({
  koot,
  lang,
}: {
  koot: CompatibilityKootEntry;
  lang: 'hi' | 'en';
}) {
  const colour = {
    strong:     'border-emerald-600/60 bg-emerald-900/15',
    moderate:   'border-sky-600/60 bg-sky-900/15',
    attention:  'border-amber-600/60 bg-amber-900/15',
    needs_work: 'border-rose-600/60 bg-rose-900/15',
  }[koot.strength];
  return (
    <div className={'rounded-md border-l-2 px-3 py-2.5 ' + colour}>
      <div className="flex items-baseline justify-between gap-3">
        <p className="text-sm font-medium text-saffron-300">
          {lang === 'hi' ? koot.name_hi : koot.name_en}
        </p>
        <p className="text-[10px] text-stone-500">
          {koot.awarded.toFixed(0)}/{koot.max_points}
        </p>
      </div>
      <p className="mt-1 text-sm leading-relaxed text-stone-100">
        {lang === 'hi' ? koot.why_works_hi : koot.why_works_en}
      </p>
      {koot.if_low_management_en && (
        <p className="mt-2 rounded bg-stone-900/50 px-2 py-1.5 text-xs italic leading-relaxed text-stone-300">
          {lang === 'hi' ? koot.if_low_management_hi : koot.if_low_management_en}
        </p>
      )}
    </div>
  );
}

function DoshaCard({
  d,
  lang,
  t,
}: {
  d: CompatibilityDoshaEntry;
  lang: 'hi' | 'en';
  t: (hi: string, en: string) => string;
}) {
  const colour = d.parihara_applies
    ? 'border-emerald-600/60 bg-emerald-900/15'
    : 'border-amber-600/60 bg-amber-900/15';
  const badge = d.parihara_applies
    ? t('परिहार लागू', 'Parihara applies')
    : t('प्रबंधन-योग्य', 'Manageable');
  const badgeColour = d.parihara_applies
    ? 'bg-emerald-900/40 text-emerald-200 ring-emerald-700/50'
    : 'bg-amber-900/40 text-amber-200 ring-amber-700/50';
  return (
    <div className={'rounded-md border-l-2 px-3 py-3 ' + colour}>
      <div className="flex items-baseline justify-between gap-3">
        <p className="text-sm font-medium text-saffron-300">
          {lang === 'hi' ? d.name_hi : d.name_en}
        </p>
        <span className={'rounded-full px-2 py-0.5 text-[10px] font-medium ring-1 ' + badgeColour}>
          {badge}
        </span>
      </div>
      {d.parihara_applies && (
        <p className="mt-2 text-xs leading-relaxed text-emerald-200">
          ✓ {lang === 'hi' ? d.parihara_reason_hi : d.parihara_reason_en}
        </p>
      )}
      <ul className="mt-2 space-y-1 text-xs leading-relaxed text-stone-200">
        {(lang === 'hi' ? d.management_steps_hi : d.management_steps_en).map((step, i) => (
          <li key={i} className="flex gap-2">
            <span className="text-saffron-400">●</span>
            <span>{step}</span>
          </li>
        ))}
      </ul>
      <p className="mt-2 border-t border-stone-800/40 pt-1 text-[10px] italic text-stone-500">
        {t('स्रोत', 'Source')}: {lang === 'hi' ? d.classical_source_hi : d.classical_source_en}
      </p>
    </div>
  );
}
